import { Yazismalar } from './Yazismalar';
import { MusteriSatisOzeti } from './MusteriSatisOzeti';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { DetayKart } from './DetayKart';
import { musteriAdiBicimle } from './hizliTeklif/musteri-adi';
import './detay.css';
import {
  api,
  ApiError,
  type CustomerChannels,
  type CustomerDetail,
  type DashboardSectionIndex,
  type GezipuanLedger,
  type Me,
  type CustomerSalesSummary,
  type Order,
} from './api';
import { KANAL_TURU, durumAdi, kalemTuru, kanalTuru, musteriDurumu, musteriTuru, para, tamSayi, tarih, tarihSaat, ORTAK } from './metin/ortak';
import { KABUK_METIN } from './metin/kabuk';
import { baglantiTiklamasi, girisYolu, musteriYolu, useYol, YOLLAR } from './yonlendirme';

/**
 * Müşteri ve sipariş detay sayfaları (CRM-A16).
 *
 * İkisi de aynı kusuru düzeltiyor: bir satıra tıklamanın hiçbir yere gitmediği, telefonun,
 * notların ve sipariş geçmişinin hiçbir ekranda görünmediği kusur. `yonlendirme.ts`teki iki
 * dinamik adres (`/musteriler/:id`, `/siparisler/:id`) bunları taşıyor — gerçek adresler,
 * kopyalanıp gönderilebilir, geri düğmesi çalışır.
 */

function describe(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return error instanceof Error ? error.message : ORTAK.hataOlustu;
}

export function CustomerDetailPage({ id, me }: { id: string; me: Me }) {
  const M = KABUK_METIN.musteriDetay;
  const { git } = useYol();
  const [customer, setCustomer] = useState<CustomerDetail | null>(null);
  const [satisOzeti, setSatisOzeti] = useState<CustomerSalesSummary | null>(null);
  const [satisOzetiHata, setSatisOzetiHata] = useState(false);
  const [gezipuan, setGezipuan] = useState<GezipuanLedger | null>(null);
  const [channels, setChannels] = useState<CustomerChannels | null>(null);
  const [noteBody, setNoteBody] = useState('');
  const [channelKind, setChannelKind] = useState<string>('WHATSAPP');
  const [channelHandle, setChannelHandle] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const noteRef = useRef<HTMLTextAreaElement | null>(null);
  const [channelBusy, setChannelBusy] = useState(false);
  const gezipuanGorebilir = me.permissions.includes('gezipuan:read');
  const kanalGorebilir = me.permissions.includes('channel_identity:read');
  const kanalEklenebilir = me.permissions.includes('channel_identity:write');
  // P0-2 (denetim, 07.09.2026): not formu her rol için çiziliyordu ama sunucu
  // `POST customers/:id/notes` için `customer:note_create` istiyor (`customers.module.ts`).
  // ADMIN bu izni tutmuyor: form dolduruluyor, düğme etkinleşiyor, sunucu 403 ile reddediyor.
  // Kanal ekleme bölümü zaten aynı deseni kullanıyordu (`kanalEklenebilir`); not formu bunu
  // kullanmıyordu.
  const notEklenebilir = me.permissions.includes('customer:note_create');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [detail, ozet, ledger, channelList] = await Promise.all([
        api.customer(id),
        // Satış özeti ayrı yakalanır: özet gelmese de müşteri kartı açılır (hata satırında söylenir).
        api.customerSalesSummary(id).catch(() => null),
        gezipuanGorebilir ? api.gezipuanLedger(id) : Promise.resolve(null),
        kanalGorebilir ? api.customerChannels(id) : Promise.resolve(null),
      ]);
      setCustomer(detail);
      setSatisOzeti(ozet);
      setSatisOzetiHata(ozet === null);
      setGezipuan(ledger);
      setChannels(channelList);
    } catch (caught) {
      setError(describe(caught));
    } finally {
      setLoading(false);
    }
  }, [id, gezipuanGorebilir, kanalGorebilir]);

  useEffect(() => {
    void load();
  }, [load]);

  const addNote = () => {
    const body = noteBody.trim();
    if (!body) {
      setError(M.notBos);
      return;
    }
    void (async () => {
      setBusy(true);
      setError(null);
      try {
        await api.addCustomerNote(id, body);
        setNoteBody('');
        await load();
      } catch (caught) {
        setError(describe(caught));
      } finally {
        setBusy(false);
      }
    })();
  };

  const addChannel = () => {
    const handle = channelHandle.trim();
    if (!handle) {
      setError(M.kanalHandleBos);
      return;
    }
    void (async () => {
      setChannelBusy(true);
      setError(null);
      try {
        await api.linkCustomerChannel(id, { kind: channelKind, handle });
        setChannelHandle('');
        await load();
      } catch (caught) {
        setError(describe(caught));
      } finally {
        setChannelBusy(false);
      }
    })();
  };

  const teklifAdresi = `${YOLLAR.hizliTeklif}?musteri=${encodeURIComponent(id)}`;
  const notaGit = () => {
    const alan = noteRef.current;
    if (!alan) return;
    alan.scrollIntoView({ block: 'center' });
    alan.focus();
  };

  return (
    <>
      <p>
        <a href={YOLLAR.musteriler} onClick={baglantiTiklamasi(git, YOLLAR.musteriler)}>
          ← {M.geriDon}
        </a>
      </p>
      {error ? (
        <div className="error callout" role="alert" data-testid="customer-detail-error">
          <span>{error}</span>
        </div>
      ) : null}
      {loading && !customer ? <p className="muted">{ORTAK.yukleniyor}</p> : null}
      {!loading && !customer && !error ? <p className="muted">{M.bulunamadi}</p> : null}
      {customer ? (
        <div className="detay-sayfa" data-testid="customer-detail" aria-labelledby="customer-detail-title" role="region">
          <header className="detay-baslik" data-testid="customer-header">
            <div className="detay-baslik-ust">
              <div className="detay-baslik-ad">
                <span className="eyebrow">{M.baslikOnek}</span>
                <h2 id="customer-detail-title">{musteriAdiBicimle(customer.displayName)}</h2>
                <div className="detay-rozetler">
                  <span className="state">{musteriTuru(customer.type)}</span>
                  <span className={`state state-${customer.statusCode.toLowerCase()}`}>{musteriDurumu(customer.statusCode)}</span>
                </div>
              </div>
              <div className="detay-eylemler" role="group" aria-label={M.hizliEylemler}>
                <a
                  className="detay-eylem detay-eylem-birincil"
                  href={teklifAdresi}
                  onClick={baglantiTiklamasi(git, teklifAdresi)}
                  data-testid="customer-quote-create"
                >
                  {M.teklifOlustur}
                </a>
                {notEklenebilir ? (
                  <button type="button" className="ikincil" data-testid="customer-note-jump" onClick={notaGit}>
                    {M.notYaz}
                  </button>
                ) : null}
              </div>
            </div>
            <dl className="detay-bilgi">
              <div>
                <dt>{M.eposta}</dt>
                <dd data-testid="customer-detail-email">
                  {customer.email ? <a href={`mailto:${customer.email}`}>{customer.email}</a> : ORTAK.belirtilmemis}
                </dd>
              </div>
              <div>
                <dt>{M.telefon}</dt>
                <dd data-testid="customer-detail-phone">
                  {customer.phone ? <a href={`tel:${customer.phone}`}>{customer.phone}</a> : ORTAK.belirtilmemis}
                </dd>
              </div>
              <div>
                <dt>{customer.type === 'INDIVIDUAL' ? M.tcKimlik : M.vergiNo}</dt>
                <dd>{(customer.type === 'INDIVIDUAL' ? customer.nationalId : customer.taxNumber) ?? ORTAK.belirtilmemis}</dd>
              </div>
              <div>
                <dt>{M.sahip}</dt>
                <dd>{customer.owner?.displayName ?? ORTAK.atanmamis}</dd>
              </div>
              <div>
                <dt>{M.olusturulma}</dt>
                <dd>{tarih(customer.createdAt)}</dd>
              </div>
            </dl>
          </header>

          <div className="detay-izgara">
            <div className="detay-ana">
              {satisOzeti ? (
                <MusteriSatisOzeti ozet={satisOzeti} git={git} />
              ) : satisOzetiHata ? (
                <p className="error callout" role="alert" data-testid="customer-sales-summary-error">{M.satisOzetiYuklenemedi}</p>
              ) : null}
            </div>

            <div className="detay-yan">
              <FaturaBilgisi customer={customer} yazabilir={me.permissions.includes('customer:update')} onSaved={load} />

              {customer.financial || !me.permissions.includes('customer:read_financial') ? (
                <DetayKart kimlik="detay-finans" baslik={M.finansBasligi}>
                  {customer.financial ? (
                    <dl className="detay-bilgi detay-bilgi-dar">
                      <div>
                        <dt>{M.krediLimiti}</dt>
                        <dd>
                          {customer.financial.creditLimitMinor === null
                            ? M.krediLimitiYok
                            : para(customer.financial.creditLimitMinor, customer.financial.billingCurrencyCode)}
                        </dd>
                      </div>
                      {customer.financial.financeNotes ? (
                        <div>
                          <dt>{M.finansNotu}</dt>
                          <dd>{customer.financial.financeNotes}</dd>
                        </div>
                      ) : null}
                    </dl>
                  ) : (
                    <p className="muted">{M.financialGizli}</p>
                  )}
                </DetayKart>
              ) : null}

              <DetayKart kimlik="detay-gezipuan" baslik={M.gezipuanBasligi} testKimligi={gezipuanGorebilir && gezipuan ? 'customer-gezipuan' : undefined}>
                {gezipuanGorebilir ? (
                  gezipuan ? (
                    <>
                      <div className="stat-grid detay-seritler detay-seritler-iki">
                        <div className="stat">
                          <span className="stat-label">{M.gezipuanHarcanabilir}</span>
                          <p className="stat-value" data-testid="customer-gezipuan-spendable">{para(gezipuan.spendableMinor, gezipuan.currency)}</p>
                        </div>
                        <div className="stat">
                          <span className="stat-label">{M.gezipuanBekleyen}</span>
                          <p className="stat-value">{para(gezipuan.pendingMinor, gezipuan.currency)}</p>
                        </div>
                        <div className="stat">
                          <span className="stat-label">{M.gezipuanHeldeki}</span>
                          <p className="stat-value">{para(gezipuan.heldMinor, gezipuan.currency)}</p>
                        </div>
                        <div className="stat">
                          <span className="stat-label">{M.gezipuanYakindaSuresiDolacak}</span>
                          <p className="stat-value">{para(gezipuan.expiringSoonMinor, gezipuan.currency)}</p>
                        </div>
                      </div>
                      {gezipuan.movements.length === 0 ? (
                        <p className="muted">{M.gezipuanYok}</p>
                      ) : (
                        <details className="detay-acilir">
                          <summary>{M.gezipuanHareketlerBasligi}</summary>
                          <div className="table-wrap">
                            <table data-testid="customer-gezipuan-movements">
                              <thead>
                                <tr>
                                  <th>{M.sutunTarihYazildi}</th>
                                  <th>{M.sutunHareketTuru}</th>
                                  <th>{M.sutunTutar}</th>
                                  <th>{M.sutunSiparis}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {gezipuan.movements.map((movement) => (
                                  <tr key={movement.id}>
                                    <td>{tarihSaat(movement.occurredAt)}</td>
                                    <td>{durumAdi(movement.kind)}</td>
                                    <td>{para(movement.amountMinor, gezipuan.currency)}</td>
                                    <td>{movement.orderId ?? ORTAK.belirtilmemis}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </details>
                      )}
                    </>
                  ) : (
                    <p className="muted">{ORTAK.yukleniyor}</p>
                  )
                ) : (
                  <p className="muted">{M.gezipuanGizli}</p>
                )}
              </DetayKart>

              <DetayKart kimlik="detay-kanal" baslik={M.kanalKimligiBasligi} testKimligi={kanalGorebilir && channels ? 'customer-channels' : undefined}>
                {kanalGorebilir ? (
                  channels ? (
                    <>
                      {channels.items.length === 0 ? (
                        <p className="muted">{M.kanalYok}</p>
                      ) : (
                        <ul className="detay-liste" data-testid="customer-channel-list">
                          {channels.items.map((channel) => (
                            <li key={channel.id} className="detay-kanal-satir">
                              <div className="detay-kanal-ana">
                                <strong>{kanalTuru(channel.kind)}</strong>
                                <span className="uzun-deger">{channel.handle}</span>
                              </div>
                              <span className={`state state-${channel.state.toLowerCase()}`}>{durumAdi(channel.state)}</span>
                              <small className="muted">{M.kanalMeta(channel.linkedBy, tarihSaat(channel.linkedAt))}</small>
                            </li>
                          ))}
                        </ul>
                      )}
                      {kanalEklenebilir ? (
                        <div className="detay-kanal-ekle" role="group" aria-label={M.kanalEkle}>
                          <label className="detay-alan">
                            <span>{M.kanalEkleEtiketiKanal}</span>
                            <select
                              data-testid="customer-channel-kind"
                              value={channelKind}
                              onChange={(event) => setChannelKind(event.target.value)}
                            >
                              {Object.keys(KANAL_TURU).map((kod) => (
                                <option key={kod} value={kod}>
                                  {kanalTuru(kod)}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label className="detay-alan">
                            <span>{M.kanalEkleEtiketiHandle}</span>
                            <input
                              type="text"
                              data-testid="customer-channel-handle"
                              maxLength={190}
                              value={channelHandle}
                              onChange={(event) => setChannelHandle(event.target.value)}
                            />
                          </label>
                          <div className="detay-form-alt">
                            <button type="button" className="ikincil" data-testid="customer-channel-add" disabled={channelBusy || !channelHandle.trim()} onClick={addChannel}>
                              {M.kanalEkle}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <p className="muted">{M.kanalYazmaGizli}</p>
                      )}
                    </>
                  ) : (
                    <p className="muted">{ORTAK.yukleniyor}</p>
                  )
                ) : (
                  <p className="muted">{M.kanalKimligiGizli}</p>
                )}
              </DetayKart>

              <DetayKart kimlik="detay-notlar" baslik={M.notlarBasligi}>
                {customer.notes && customer.notes.length > 0 ? (
                  <ul className="detay-liste detay-notlar" data-testid="customer-notes">
                    {customer.notes.map((note) => (
                      <li key={note.id}>
                        <p>{note.body}</p>
                        <small className="muted">{tarihSaat(note.createdAt)}</small>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="muted">{M.notYok}</p>
                )}
                {notEklenebilir ? (
                  <div className="detay-not-ekle">
                    <div className="detay-alan">
                      <label htmlFor="detay-not-girdi">{M.notEkleEtiketi}</label>
                      <textarea
                        id="detay-not-girdi"
                        ref={noteRef}
                        data-testid="customer-note-input"
                        aria-describedby="detay-not-ipucu"
                        maxLength={1000}
                        value={noteBody}
                        onChange={(event) => setNoteBody(event.target.value)}
                      />
                      <small id="detay-not-ipucu" className="muted">{M.notIpucu}</small>
                    </div>
                    <div className="detay-form-alt">
                      <button type="button" className="birincil" data-testid="customer-note-add" disabled={busy || !noteBody.trim()} onClick={addNote}>
                        {M.notEkle}
                      </button>
                    </div>
                  </div>
                ) : null}
              </DetayKart>
            </div>
          </div>
        </div>
      ) : null}
      {me.permissions.includes('conversation:read') ? <Yazismalar key={id} me={me} customerId={id} /> : null}
    </>
  );
}

export function OrderDetailPage({ id, sections }: { id: string; me: Me; sections: DashboardSectionIndex | null }) {
  const M = KABUK_METIN.siparisDetay;
  const { git } = useYol();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // P0-3 (denetim, 07.09.2026): geri bağlantısı her rol için sabit `/operasyon`a gidiyordu.
  // Operasyon bölümünü görmeyen bir rol (ör. CRM_SALES) tıklayınca "Bu rol hiçbir bölüme
  // atanmadı" ekranına düşüyordu, başlık ise tıkladığı bölümün adını taşımaya devam ediyordu.
  // `girisYolu` zaten aynı soruyu giriş anında cevaplıyor: rolün gördüğü tek bölüm varsa oraya,
  // yoksa genel bakışa. Aynı mantık burada da doğru cevap.
  const geriYolu = sections ? girisYolu(sections.sections) : YOLLAR.genelBakis;

  useEffect(() => {
    setLoading(true);
    setError(null);
    api
      .order(id)
      .then(setOrder)
      .catch((caught) => setError(describe(caught)))
      .finally(() => setLoading(false));
  }, [id]);

  return (
    <>
      <p>
        <a href={geriYolu} onClick={baglantiTiklamasi(git, geriYolu)}>
          ← {M.geriDon}
        </a>
      </p>
      {error ? (
        <div className="error callout" role="alert" data-testid="order-detail-error">
          <span>{error}</span>
        </div>
      ) : null}
      {loading && !order ? <p className="muted">{ORTAK.yukleniyor}</p> : null}
      {!loading && !order && !error ? <p className="muted">{M.bulunamadi}</p> : null}
      {order ? (
        <section className="content-section" data-testid="order-detail" aria-labelledby="order-detail-title">
          <div className="section-heading">
            <div>
              <span className="eyebrow">{M.baslikOnek}</span>
              <h2 id="order-detail-title">{order.code}</h2>
            </div>
            <span className="state">{durumAdi(order.status)}</span>
          </div>

          <div className="stat-grid">
            <div className="stat">
              <span className="stat-label">{M.musteri}</span>
              <p className="stat-value">
                <a href={musteriYolu(order.customer.id)} onClick={baglantiTiklamasi(git, musteriYolu(order.customer.id))} data-testid="order-customer-link">
                  {order.customer.displayName}
                </a>
              </p>
            </div>
            <div className="stat">
              <span className="stat-label">{M.toplam}</span>
              <p className="stat-value">{para(order.revenueTotalMinor, order.currencyCode)}</p>
            </div>
            <div className="stat">
              <span className="stat-label">{M.surum}</span>
              <p className="stat-value">{tamSayi(order.quoteVersion.versionNo)}</p>
            </div>
          </div>

          <h3>{M.kalemlerBasligi}</h3>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{M.sutunKalemTuru}</th>
                  <th>{M.sutunAciklama}</th>
                  <th>{M.sutunTedarikci}</th>
                  <th>{M.sutunAdet}</th>
                  <th>{M.sutunBirimFiyat}</th>
                  <th>{M.sutunToplam}</th>
                </tr>
              </thead>
              <tbody>
                {order.items.map((item) => (
                  <tr key={item.id}>
                    <td>{kalemTuru(item.itemType)}</td>
                    <td>{item.description}</td>
                    <td>{item.supplierName}</td>
                    <td>{tamSayi(item.quantity)}</td>
                    <td>{para(item.unitPriceMinor, order.currencyCode)}</td>
                    <td>{para(item.lineTotalMinor, order.currencyCode)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3>{M.gorevlerBasligi}</h3>
          {order.tasks.length === 0 ? (
            <p className="muted">{M.gorevYok}</p>
          ) : (
            <ul>
              {order.tasks.map((task) => (
                <li key={task.id}>{M.gorevSatiri(task.code, durumAdi(task.status))}</li>
              ))}
            </ul>
          )}

          <h3>{M.mutabakatBasligi}</h3>
          {order.reconciliation ? (
            <div className="stat-grid">
              <div className="stat">
                <span className="stat-label">{M.durum}</span>
                <p className="stat-value">{durumAdi(order.reconciliation.status)}</p>
              </div>
              <div className="stat">
                <span className="stat-label">{ORTAK.acikBakiye}</span>
                <p className="stat-value">{para(order.reconciliation.outstandingMinor, order.currencyCode)}</p>
              </div>
            </div>
          ) : (
            <p className="muted">{M.mutabakatYok}</p>
          )}
        </section>
      ) : null}
    </>
  );
}

/**
 * Fatura bilgisi (04.10.2026). `Customer.billingAddress` şemada vardı ama hiçbir ekran ve hiçbir uç
 * yazmıyordu; kesilecek fatura kuyruğundaki "adres" eksiği bu yüzden kapatılamıyordu.
 * Düzen (03.11.2026): etiket üstte, girdi tam genişlikte, iki grup (adres ve ülke, kimlik), kaydet düğmesi
 * formun altında. Yalnız sunum; gönderilen alanlar ve yetki aynı.
 */
function FaturaBilgisi({ customer, yazabilir, onSaved }: { customer: CustomerDetail; yazabilir: boolean; onSaved: () => Promise<void> }) {
  const M = KABUK_METIN.musteriDetay;
  const bireysel = customer.type === 'INDIVIDUAL';
  const kok = useId();
  const [adres, setAdres] = useState(customer.billingAddress ?? '');
  const [ulke, setUlke] = useState(customer.countryCode ?? '');
  const [kimlik, setKimlik] = useState((bireysel ? customer.nationalId : customer.taxNumber) ?? '');
  const [pasaport, setPasaport] = useState(customer.passportNumber ?? '');
  const [mesgul, setMesgul] = useState(false);
  const [hata, setHata] = useState<string | null>(null);
  const [tamam, setTamam] = useState(false);

  const kaydet = () => {
    void (async () => {
      setMesgul(true); setHata(null); setTamam(false);
      try {
        await api.updateCustomerBilling(customer.id, {
          expectedVersion: customer.version,
          ...(adres.trim() ? { billingAddress: adres.trim() } : {}),
          ...(ulke.trim() ? { countryCode: ulke.trim().toUpperCase() } : {}),
          ...(kimlik.trim() ? (bireysel ? { nationalId: kimlik.trim() } : { taxNumber: kimlik.trim() }) : {}),
          ...(bireysel && pasaport.trim() ? { passportNumber: pasaport.trim().toUpperCase() } : {}),
        });
        setTamam(true);
        await onSaved();
      } catch (caught) {
        setHata(describe(caught));
      } finally {
        setMesgul(false);
      }
    })();
  };

  return (
    <DetayKart kimlik="detay-fatura" baslik={M.faturaBasligi} testKimligi="customer-billing">
      <p className="muted detay-tarif">{M.faturaTarif}</p>
      {hata ? <div className="error callout" role="alert">{hata}</div> : null}
      {tamam ? <div className="callout" role="status">{M.faturaKaydedildi}</div> : null}
      {yazabilir ? (
        <form
          className="detay-form"
          onSubmit={(olay) => {
            olay.preventDefault();
            kaydet();
          }}
        >
          <fieldset className="detay-grup">
            <legend>{M.faturaGrupAdres}</legend>
            <div className="detay-alan">
              <label htmlFor={`${kok}-adres`}>{M.faturaAdresi}</label>
              <input id={`${kok}-adres`} data-testid="customer-billing-address" autoComplete="street-address" aria-describedby={`${kok}-adres-ipucu`} value={adres} maxLength={140} onChange={(olay) => setAdres(olay.target.value)} />
              <small id={`${kok}-adres-ipucu`} className="muted">{M.faturaAdresiIpucu}</small>
            </div>
            <div className="detay-alan detay-alan-dar">
              <label htmlFor={`${kok}-ulke`}>{M.ulkeKodu}</label>
              <input id={`${kok}-ulke`} data-testid="customer-billing-country" autoComplete="country" autoCapitalize="characters" aria-describedby={`${kok}-ulke-ipucu`} value={ulke} maxLength={2} onChange={(olay) => setUlke(olay.target.value)} />
              <small id={`${kok}-ulke-ipucu`} className="muted">{M.ulkeKoduIpucu}</small>
            </div>
          </fieldset>
          <fieldset className="detay-grup">
            <legend>{M.faturaGrupKimlik}</legend>
            <div className="detay-alan">
              <label htmlFor={`${kok}-kimlik`}>{bireysel ? M.tcKimlik : M.vergiNo}</label>
              <input id={`${kok}-kimlik`} data-testid="customer-billing-identity" inputMode={bireysel ? 'numeric' : undefined} value={kimlik} maxLength={bireysel ? 11 : 40} onChange={(olay) => setKimlik(olay.target.value)} />
            </div>
            {bireysel ? (
              <div className="detay-alan">
                <label htmlFor={`${kok}-pasaport`}>{M.pasaport}</label>
                <input id={`${kok}-pasaport`} data-testid="customer-billing-passport" value={pasaport} maxLength={20} onChange={(olay) => setPasaport(olay.target.value)} />
              </div>
            ) : null}
          </fieldset>
          <div className="detay-form-alt">
            <button type="submit" className="birincil" disabled={mesgul}>{mesgul ? M.faturaKaydediliyor : M.faturaKaydet}</button>
          </div>
        </form>
      ) : (
        <>
          <dl className="detay-bilgi detay-bilgi-dar">
            <div>
              <dt>{M.faturaAdresi}</dt>
              <dd>{customer.billingAddress ?? ORTAK.belirtilmemis}</dd>
            </div>
          </dl>
          <p className="muted">{M.faturaYalnizOkuma}</p>
        </>
      )}
    </DetayKart>
  );
}
